import type { ExecutionEventType } from '@ctcells/contract'
import { useMemo, useSyncExternalStore } from 'react'

/**
 * 구독이 나르는 **배치 진행**의 보관소. `batch_progress` 는 REST 에 대응하는 값이 없어서
 * 무효화(`bridge.ts`)로는 화면에 도달하지 못한다 — 그 한 축만 여기 보관한다.
 *
 * 🔴 **왜 쿼리 캐시가 아닌가.** 캐시 키는 「서버에 다시 물으면 같은 값이 오는 것」의 자리다.
 *    진행은 다시 물을 창구가 없다(`GET /batches` 13필드에 진행이 0건 · `docs/backend-requests.csv`
 *    BR-09). 없는 창구의 캐시를 만들면 무효화가 그 값을 **지워 버린다.**
 * 🔴 **UI 상태도 아니다** — 서버가 말한 사실이라 클라 스토어(zustand)가 아니라 구독 계층에 둔다.
 *
 * 🔴 **지어내지 않는다.** 여기 들어오는 값은 서버 프레임에 실제로 있는 것뿐이다.
 *    `step`·`total` 로 백분율을 만들지 않는다 — eric 지시가 ctcells-core 에 박혀 있다
 *    (`app/resources/admin/pages/dashboard.js` — 「프론트에서 계산하는 게 아니고 서버에서
 *    계산해서 보내줘야 한다」 2026-09-15 · 그 파일에 산술 연산이 0건임을 백엔드 시험이 단언).
 *
 * 실물 프레임(코드 3단 대조 2026-09-20 — `module-core` `core/publisher._publish_execution` →
 * `ctcells-core` `core/module_subscriber._execution` → `views/stream_relay._execution`)
 *
 * ```json
 * { "type": "batch_progress", "ts": "…", "module_id": "cent-01",
 *   "source_version": 7, "batch_id": "B-0912",
 *   "payload": { "ts": "…", "type": "progress", "version": 7,
 *                "execution_id": "exec-7", "step": 2, "total": 9, "message": "…" } }
 * ```
 *
 * ⚠ 봉투의 `batch_id` 는 **모듈 미러의 `execution_id` 가 일치할 때만** 실린다(서버가 배치를
 *   지어내지 않는다). 없을 때 잇는 방법도 서버가 정해 두었다 — `module_status` 의
 *   `current_execution{execution_id, batch_id}` 다(`views/stream_relay._execution` 머리말).
 *   이 파일의 리듀서는 ctcells-core 관리자 화면(`app/resources/admin/batch_panel.js`)과 같은 규칙이다.
 */

/** 다른 축도 같은 방식으로 얹으려면 여기 갈래를 늘린다 — 봉투 `type` 하나로 가른다 */
const TYPE_BATCH_PROGRESS = 'batch_progress'
const TYPE_MODULE_STATUS = 'module_status'

/**
 * 추적할 배치 수 상한. 진행은 종착해도 지워 줄 프레임이 없어서(`complete` 뒤에 아무것도 안 온다)
 * 그대로 두면 오래 켜 둔 단말에서 무한히 는다. **넘치면 오래된 것부터 버린다** —
 * 지금 도는 작업은 매 프레임 갱신되므로 항상 최신 쪽에 있다.
 */
const MAX_TRACKED_KEYS = 64

/** 작업 줄이 읽는 진행 한 건. **모든 칸이 선택이다 — 서버가 실은 것만 찬다** */
export interface BatchProgressView {
  /**
   * 모듈이 보고한 **「모듈 내」 스텝**. 배치 전체의 진행이 아니다.
   * ⚠ `0` 은 「없다」가 아니라 **첫 스텝**이다(`started` 가 `step=0`·`total=N` 으로 온다).
   */
  step?: number
  total?: number
  /** 실행 생애 축(`ExecutionStream.type`). `progress` 가 보통이고 종착은 `complete`·`error` … */
  eventType?: ExecutionEventType
  /** 모듈이 사람에게 한 말. 없으면 키 자체가 안 온다 — 빈 문자열로 채우지 않는다 */
  message?: string
  /**
   * 그 스텝의 **동작 이름 원값**(`move_to` · `place` · `expose_uv` · `discard_tip` …).
   * 🔴 한글 표기로 바꾸지 않는다 — 표시 계층의 몫이다(`moduleType` 과 같은 결).
   *
   * 🔴 **출처가 두 가지다. 서버가 같은 값을 두 꼴로 준다**(실물 20,855 프레임 전수 2026-09-21).
   *    ⓐ `step_complete` — `result.sequence` 에 **구조화된 칸**으로 온다(4,820 중 2,383건)
   *    ⓑ `progress`(시작) — `result` 가 **아예 없고** `message` 문자열
   *       (`"move_to 시작"`)에서 뽑는 수밖에 없다
   *
   * ⚠ **ⓑ 는 깨지기 쉬운 자리다.** 서버 문구가 바뀌면 조용히 빈 값이 된다 — 그래서
   *   **못 읽으면 `undefined`(모름)로 두고** 화면이 아무 구성요소도 강조하지 않게 한다.
   *   「알 수 없는 동작」 같은 말을 지어내지 않는다. 지금 미매치가 0건이라는 사실은
   *   `progress.test.ts` 가 실물 프레임 전수로 고정한다 — 문구가 바뀌면 그 시험이 먼저 깨진다.
   * ⇒ 두 꼴을 하나로 합쳐 달라는 요청이 `docs/backend-requests.csv` BR-46 이다.
   */
  action?: string
  /**
   * 실행 중 시퀀스 이름. 🔴 **진행 프레임에 없다** — `module_status` 의
   * `current_execution.sequence` 가 나른다(계약이 그렇게 갈라 두었다).
   */
  sequence?: string
  executionId?: string
  /** 그 진행을 말한 모듈 */
  moduleId?: string
  /**
   * 그 모듈의 종류 원값(`centrifuge` · `assembler` · `staining` · `collaborative_6axis`).
   * `module_status` 가 나른다 — 화면이 `moduleId` 에서 «읽어내지» 않는다.
   * 🔴 한글 표기로 바꾸는 일은 여기서 하지 않는다. 표시 계층(`@ctcells/i18n`)의 몫이다.
   */
  moduleType?: string
  /** 봉투 시각(ISO). 화면이 이 값으로 경과를 «계산하지 않는다» */
  ts?: string
  /**
   * **이 배치에서 지금까지 «들은» 모듈별 스텝 수.** 끝난 모듈도 남는다.
   *
   * 🔴 **왜 필요한가**(eric 2026-09-23): 작업 줄 오른끝이 「전체 스텝에서의 현재 스텝」이어야
   *    하는데, 프레임 한 건이 말하는 `step`·`total` 은 **그 모듈 안의 수**다. 전체를 세려면
   *    끝난 모듈의 총수를 기억하고 있어야 한다 — `byBatch` 는 마지막 한 건만 쥐고,
   *    `byModule`(상태 미러)은 모듈이 끝나면 **지워진다**(`current_execution: null`).
   * 🔴 **여기 있는 것은 «들은» 모듈뿐이다.** 아직 시작 안 한 모듈의 총수는 어디에도 없다 —
   *    총수는 모듈이 레시피를 컴파일한 결과이고 `started` 프레임으로 처음 온다(실물 확인).
   *    ⇒ 합계 분모는 **아직 늘 수 있다.** 화면이 그 사실을 드러낸다(`stripStepCount`).
   */
  moduleSteps?: readonly ModuleStepView[]
}

/**
 * 배치 한 건 안에서 **모듈 하나가 말한 스텝 수.**
 * ⚠ 단위는 **모듈이 컴파일한 스텝**이다 — 레시피의 `step_counts` 와 **다른 단위**라 섞어 더하지 않는다
 *   (실측 2026-09-23: 같은 배치에서 레시피 57·101·71 ↔ 모듈 96·164·359).
 */
export interface ModuleStepView {
  moduleId: string
  /** `module_status` 가 나른 종류 원값. 못 들었으면 없다 */
  moduleType?: string
  /** ⚠ `0` 은 첫 스텝이다 */
  step?: number
  total?: number
}

/**
 * `module_status.current_execution` 미러 한 건.
 *
 * 🔴 **스텝 수도 여기 있다**(2026-09-22 실측). 계약이 그 칸을 선언해 두었고
 *    (`ModuleSnapshotCurrent_execution.step`·`total`) 실물이 채워서 준다 —
 *    모듈이 **보류로 서 있는 동안** `batch_progress` 가 한 건도 안 오는데
 *    (WS 20초 탭: `module_status` 79건 · `batch_progress` **0건**) 이 미러에는
 *    `step:56 · total:313` 이 **그대로 실려 있었다.**
 * ⚠ 그래서 종전 작업 줄은 **가장 나쁜 때 가장 적게 말했다** — 멈춘 순간 스텝 수를 잃고
 *   시퀀스 이름만 남았다.
 */
interface ModuleExecution {
  executionId: string | undefined
  batchId: string | undefined
  sequence: string | undefined
  /** ⚠ `0` 을 떨어뜨리지 않는다 — 첫 스텝이 `0` 이다 */
  step: number | undefined
  total: number | undefined
}

export interface StreamProgressState {
  /** `batch_progress` 가 채운다. 키는 배치 ID */
  readonly byBatch: ReadonlyMap<string, BatchProgressView>
  /** `module_status` 가 채운다. 키는 모듈 ID */
  readonly byModule: ReadonlyMap<string, ModuleExecution>
  /**
   * 모듈 ID → `module_type` 원값. `module_status` 가 매번 같이 실어 준다.
   *
   * 🔴 **`byModule` 과 따로 두는 이유**: 그쪽은 「도는 실행」의 자리라 `current_execution` 이
   *    `null` 이 되면 **지워진다.** 종류는 그때도 사실이므로 같이 지우면 안 된다.
   * 🔴 **추론이 아니다.** `staining-1` 같은 ID 에서 종류를 «읽어내지» 않는다 — 서버가 준 칸을
   *    그대로 보관한다. ID 꼴은 계약이 정한 것이 아니라 등록할 때 사람이 붙인 이름이다.
   */
  readonly typeByModule: ReadonlyMap<string, string>
  /**
   * 배치 → (모듈 → 그 모듈이 말한 스텝 수). 🔴 **끝난 모듈도 지우지 않는다** — 전체 스텝을
   * 세는 재료가 이것뿐이다(`BatchProgressView.moduleSteps` 머리말).
   */
  readonly byBatchModule: ReadonlyMap<string, ReadonlyMap<string, ModuleStepView>>
}

const EMPTY: StreamProgressState = {
  byBatch: new Map(),
  byModule: new Map(),
  typeByModule: new Map(),
  byBatchModule: new Map(),
}

let state: StreamProgressState = EMPTY
const listeners = new Set<() => void>()

/**
 * **끝난 실행의 작업 짝**(실행 ID → 배치 ID) — 모듈이 실행을 놓은 «뒤에» 도착한 끝 프레임을 귀속하려고 잠시 쥔다
 * (eric 2026-10-06 「장비랑 시료 프로세스.. 종료하고.. 이벤트가 오면 처리하는데.. 중간중간에 빠졌어」).
 *
 * 🔴 **왜 필요한가** — 실행이 끝나는 순간 모듈은 `current_execution: null` 상태와 `batch_progress complete` 를 거의 같이 낸다
 *    (dev2 실측: 원심 `complete` 와 `WAITING_FOR_TRAY_UNLOAD` 가 같은 초 · `followRunningDevice.ts`). 서버는 끝 프레임 봉투에
 *    `batch_id` 를 모듈 상태 미러로 싣는데(`module_subscriber._batch_of_execution`), 상태가 먼저 적히면 그 칸이 빈 채로 온다.
 *    그때 `byModule` 은 이미 지워져 `batchOfExecution()` 이 못 찾고, **끝 프레임이 조용히 버려졌다** — 화면이 마지막 스텝에 머문다.
 * 🔴 사실만 옮긴다 — 서버가 «같은 실행 ID 로» 말한 짝(미러 · 봉투)만 담는다. 지어내지 않는다.
 * 상한 `ENDED_EXECUTION_LIMIT` — 오래된 것부터 빠진다. 끊기면(`resetStreamProgress`) 비운다.
 */
const ENDED_EXECUTION_LIMIT = 64
let executionBatches = new Map<string, string>()

function rememberExecution(executionId: string | undefined, batchId: string | undefined): void {
  if (executionId === undefined || batchId === undefined) return
  if (executionBatches.get(executionId) === batchId) return
  executionBatches.delete(executionId)
  executionBatches.set(executionId, batchId)
  while (executionBatches.size > ENDED_EXECUTION_LIMIT) {
    const oldest = executionBatches.keys().next().value
    if (oldest === undefined) break
    executionBatches.delete(oldest)
  }
}

function publish(next: StreamProgressState): void {
  if (next === state) return
  state = next
  for (const listener of listeners) listener()
}

// ── 프레임 읽기 — 서버가 준 모양대로만 읽는다 ───────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asText(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

/** ⚠ `0` 을 떨어뜨리지 않는다 — 첫 스텝이 `0` 이다 */
function asCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function put<T>(map: ReadonlyMap<string, T>, key: string, value: T): Map<string, T> {
  const next = new Map(map)
  // 맨 뒤로 다시 넣어 삽입 차례가 곧 최근 차례가 되게 한다(상한 정리의 근거)
  next.delete(key)
  next.set(key, value)
  while (next.size > MAX_TRACKED_KEYS) {
    const oldest = next.keys().next()
    if (oldest.done === true) break
    next.delete(oldest.value)
  }
  return next
}

/**
 * 배치 안의 **모듈별 스텝 수**를 갱신한다. 🔴 **덮어쓰되 지우지 않는다** — 끝난 모듈의
 * 총수가 남아 있어야 전체를 셀 수 있다.
 * 🔴 **모르는 칸은 종전 값을 지키지 않는다**(프레임 한 건이 완전한 봉투다)는 규칙의 예외가
 *    하나 있다: `total` 은 `started` 프레임에만 실리고 이후 프레임에도 계속 오지만, 미러가
 *    총수를 안 싣는 순간이 있어 **`undefined` 면 종전 값을 남긴다.** 수를 잃는 쪽이 더 나쁘다.
 */
function putModuleStep(
  map: ReadonlyMap<string, ReadonlyMap<string, ModuleStepView>>,
  batchId: string,
  next: ModuleStepView,
): ReadonlyMap<string, ReadonlyMap<string, ModuleStepView>> | undefined {
  const inner = map.get(batchId) ?? new Map<string, ModuleStepView>()
  const prev = inner.get(next.moduleId)
  const merged: ModuleStepView = {
    moduleId: next.moduleId,
    ...(next.step === undefined
      ? prev?.step === undefined
        ? {}
        : { step: prev.step }
      : { step: next.step }),
    ...(next.total === undefined
      ? prev?.total === undefined
        ? {}
        : { total: prev.total }
      : { total: next.total }),
  }
  if (prev !== undefined && prev.step === merged.step && prev.total === merged.total)
    return undefined
  const nextInner = new Map(inner)
  nextInner.set(next.moduleId, merged)
  return put(map, batchId, nextInner)
}

/**
 * 프레임 한 건을 보관소에 넣는다. **모르는 갈래는 아무것도 하지 않는다.**
 *
 * 🔴 귀속처(배치)를 모르는 진행은 **버린다.** 「지금 도는 배치」로 때우면 실행이 배치보다
 *    오래 살 때 **엉뚱한 배치에 진행이 붙는다**(서버가 같은 이유로 지어내지 않는다).
 *    나중에 `module_status` 가 사상을 주면 **그 다음 진행 건부터** 붙는다 — 소급하지 않는다.
 */
export function pushStreamProgress(raw: unknown): void {
  const frame = asRecord(raw)
  if (frame === null) return
  const type = frame['type']
  if (type === TYPE_MODULE_STATUS) {
    applyModuleStatus(frame)
    return
  }
  if (type === TYPE_BATCH_PROGRESS) applyBatchProgress(frame)
}

/**
 * **스냅샷 1회로 보관소의 빈 자리를 채운다**(DP-01 「스냅샷 1회 + 구독」의 스냅샷 쪽 · 2026-10-06).
 *
 * 🔴 **왜 필요한가**(쵸단 실측 2026-10-06): 보관소는 구독 프레임만 받아서, 화면을 연 직후 첫 `module_status`(1Hz)가
 *    오기 전에는 비어 있다 — 그 사이 작업 줄이 지금 도는 장비까지 「대기 · 전체 수량 집계 중」으로 세웠다.
 *    `GET /modules` 의 `current_execution` 은 `module_status.payload` 와 **같은 꼴**(`ModuleSnapshot` 동형 · bridge.ts 머리말)
 *    이라 그 한 건을 프레임처럼 넣는다.
 * 🔴 **들은 적 없는 모듈에만 넣는다** — 구독이 이미 말한 모듈은 건너뛴다. 스냅샷은 프레임보다 늦을 수 있어(무효화로 다시 받은
 *    목록), 덮으면 더 새로운 값을 옛 값으로 되돌린다.
 * ⚠ 새 조회가 아니다 — 작업 줄이 이미 받는 목록(`useLeftoverExecutions` 와 같은 캐시)을 넘겨받을 뿐이다.
 */
export function seedStreamProgress(modules: readonly unknown[]): void {
  for (const raw of modules) {
    const module = asRecord(raw)
    const moduleId = asText(module?.['module_id'])
    if (module === null || moduleId === undefined) continue
    if (state.typeByModule.has(moduleId) || state.byModule.has(moduleId)) continue
    applyModuleStatus({ type: TYPE_MODULE_STATUS, module_id: moduleId, payload: module })
  }
}

function applyModuleStatus(frame: Record<string, unknown>): void {
  const payload = asRecord(frame['payload'])
  const moduleId = asText(frame['module_id']) ?? asText(payload?.['module_id'])
  if (moduleId === undefined || payload === null) return

  /*
   * 🔴 종류는 **`current_execution` 유무와 무관하게** 먼저 담는다. 아래 두 조기 반환보다
   *    앞이어야 한다 — 실행이 없는 모듈(대부분의 시간)도 종류는 알려 주고 있고, 그것이
   *    3행의 「어느 모듈인가」를 만드는 재료다.
   */
  const moduleType = asText(payload['module_type'])
  let typeByModule = state.typeByModule
  if (moduleType !== undefined && typeByModule.get(moduleId) !== moduleType) {
    typeByModule = put(typeByModule, moduleId, moduleType)
    publish({
      byBatch: state.byBatch,
      byModule: state.byModule,
      typeByModule,
      byBatchModule: state.byBatchModule,
    })
  }

  // ⚠ **키가 없는 것**(모르는 스냅샷)과 **`null`**(도는 실행이 없다)을 가른다
  if (!('current_execution' in payload)) return

  const exec = asRecord(payload['current_execution'])
  if (exec === null) {
    const ended = state.byModule.get(moduleId)
    if (ended === undefined) return
    // 놓은 실행의 짝은 남긴다 — 끝 프레임이 이 상태보다 늦게 와도 작업에 붙는다(`executionBatches`)
    rememberExecution(ended.executionId, ended.batchId)
    const byModule = new Map(state.byModule)
    byModule.delete(moduleId)
    /*
     * 🔴 **`byBatchModule` 은 «지우지 않는다».** 실행이 끝나면 미러가 `null` 이 되는데,
     *    그때 스텝 수까지 버리면 **끝난 모듈의 총수**를 잃어 전체를 못 센다(eric 2026-09-23).
     */
    publish({ byBatch: state.byBatch, byModule, typeByModule, byBatchModule: state.byBatchModule })
    return
  }

  const next: ModuleExecution = {
    executionId: asText(exec['execution_id']),
    batchId: asText(exec['batch_id']),
    sequence: asText(exec['sequence']),
    step: asCount(exec['step']),
    total: asCount(exec['total']),
  }
  rememberExecution(next.executionId, next.batchId)
  /*
   * 🔴 **미러도 모듈별 스텝 수의 출처다.** 모듈이 보류로 서 있는 동안 `batch_progress` 가
   *    0건인데 이 미러에는 `step`·`total` 이 실려 온다(2026-09-22 실측) — 그 순간에도
   *    그 모듈 칸이 비지 않게 한다.
   */
  let byBatchModule = state.byBatchModule
  if (next.batchId !== undefined && (next.step !== undefined || next.total !== undefined)) {
    const updated = putModuleStep(byBatchModule, next.batchId, {
      moduleId,
      ...(next.step === undefined ? {} : { step: next.step }),
      ...(next.total === undefined ? {} : { total: next.total }),
    })
    if (updated !== undefined) byBatchModule = updated
  }

  const prev = state.byModule.get(moduleId)
  if (
    byBatchModule === state.byBatchModule &&
    prev !== undefined &&
    prev.executionId === next.executionId &&
    prev.batchId === next.batchId &&
    prev.sequence === next.sequence &&
    prev.step === next.step &&
    prev.total === next.total
  ) {
    return // 1초 주기로 같은 값이 온다 — 같으면 알리지 않는다(렌더 억제)
  }
  publish({
    byBatch: state.byBatch,
    byModule: put(state.byModule, moduleId, next),
    typeByModule: state.typeByModule,
    byBatchModule,
  })
}

function applyBatchProgress(frame: Record<string, unknown>): void {
  const payload = asRecord(frame['payload']) ?? {}
  const executionId = asText(payload['execution_id'])
  const batchId = asText(frame['batch_id']) ?? batchOfExecution(executionId)
  if (batchId === undefined) return
  rememberExecution(executionId, batchId)

  // 🔴 **병합하지 않는다.** 프레임 하나가 그 실행의 «완전한» 봉투다 — 이전 프레임의
  //    `message` 를 남기면 서버가 지운 말이 화면에 계속 붙는다.
  const view: BatchProgressView = {}
  const step = asCount(payload['step'])
  if (step !== undefined) view.step = step
  const total = asCount(payload['total'])
  if (total !== undefined) view.total = total
  const eventType = executionEventType(payload['type'])
  if (eventType !== undefined) view.eventType = eventType
  const message = asText(payload['message'])
  if (message !== undefined) view.message = message
  const action = stepAction(payload)
  if (action !== undefined) view.action = action
  if (executionId !== undefined) view.executionId = executionId
  const moduleId = asText(frame['module_id'])
  if (moduleId !== undefined) view.moduleId = moduleId
  const ts = asText(frame['ts']) ?? asText(payload['ts'])
  if (ts !== undefined) view.ts = ts

  /* 🔴 모듈별 수를 «따로» 쌓는다 — `byBatch` 는 마지막 한 건만 쥐기 때문이다 */
  let byBatchModule = state.byBatchModule
  if (moduleId !== undefined && (step !== undefined || total !== undefined)) {
    const updated = putModuleStep(byBatchModule, batchId, {
      moduleId,
      ...(step === undefined ? {} : { step }),
      ...(total === undefined ? {} : { total }),
    })
    if (updated !== undefined) byBatchModule = updated
  }

  publish({
    byBatch: put(state.byBatch, batchId, view),
    byModule: state.byModule,
    typeByModule: state.typeByModule,
    byBatchModule,
  })
}

/**
 * 시작 프레임의 동작 이름을 싣고 있는 **유일한 자리**. 실물 전수에서 이 꼴을 벗어난 건이
 * 0건이었다 — 그래도 「반드시 맞는다」로 쓰지 않는다(아래 호출부가 미매치를 `undefined` 로 둔다).
 */
const ACTION_IN_MESSAGE = /^(\S+) 시작$/

/**
 * 그 스텝의 동작 이름. **구조화된 칸을 먼저 본다** — 문자열 파싱은 그 칸이 없을 때의 차선이다.
 * 🔴 둘 다 실패하면 `undefined` 다. 원값을 지어내거나 빈 문자열로 때우지 않는다.
 *
 * 순서가 셋인 이유 — **오늘(2026-09-22) 실물이 늘었다.**
 *
 * | 자리 | 언제 오나 | 관측(929프레임 중 `batch_progress` 270건) |
 * | --- | --- | --- |
 * | `payload.sequence` | `started` 말고 **전부** | 268/270. 🔴 **새로 생겼다** — 어제까지 payload 키는 8개였고 이 칸이 없었다 |
 * | `payload.result.sequence` | `step_complete` 뿐 | 133/270 |
 * | `payload.message` 파싱 | 나머지 | `move_to 시작` 꼴 |
 *
 * 🔴 **`sequence` 를 맨 앞에 둔다.** 세 자리가 같은 값을 말하는 것을 실물로 확인했지만
 *    (270건 전수 대조 — 어긋난 건 0), 글자를 정규식으로 뜯는 것은 **서버가 문구를 바꾸면
 *    그날로 죽는 길**이다. 구조화된 칸이 왔으니 그쪽이 1순위다.
 * ⚠ `started`(「실행 시작 — 스텝 164개」)에는 세 자리 다 없다 — 그때는 `undefined` 가 맞다.
 *   아직 아무 동작도 시작하지 않은 프레임이라 지어낼 이름이 없다.
 */
export function stepAction(payload: Record<string, unknown>): string | undefined {
  const fromField = asText(payload['sequence'])
  if (fromField !== undefined) return fromField
  const fromResult = asText(asRecord(payload['result'])?.['sequence'])
  if (fromResult !== undefined) return fromResult
  const message = asText(payload['message'])
  if (message === undefined) return undefined
  return ACTION_IN_MESSAGE.exec(message)?.[1]
}

/** `ExecutionStream.type` 어휘 전수. 사전에 없는 값은 **버린다**(원값을 화면에 흘리지 않는다) */
const EXECUTION_EVENT_TYPES: readonly ExecutionEventType[] = [
  'started',
  'progress',
  'telemetry',
  'step_complete',
  'complete',
  'error',
  'cancelled',
  'aborted',
  'paused',
  'resumed',
]

export function executionEventType(value: unknown): ExecutionEventType | undefined {
  return typeof value === 'string' && (EXECUTION_EVENT_TYPES as readonly string[]).includes(value)
    ? (value as ExecutionEventType)
    : undefined
}

/** 실행 → 배치 — 도는 실행(미러)을 먼저, 없으면 막 끝난 실행의 짝(`executionBatches`) */
export function batchOfExecution(executionId: string | undefined): string | undefined {
  if (executionId === undefined) return undefined
  for (const exec of state.byModule.values()) {
    if (exec.executionId === executionId) return exec.batchId
  }
  return executionBatches.get(executionId)
}

// ── 비우기 ──────────────────────────────────────────────────────────────────

/**
 * 보관한 진행을 **전부 버린다.**
 *
 * 🔴 구독이 끊기면 부른다. 이 채널에는 `retain` 이 없어 **끊긴 동안의 사건은 다시 오지 않고**
 *    (`views/stream_relay._connection` 머리말), 그러면 재연결 직후에 남아 있는 마지막 값이
 *    「지금」인 척하게 된다. 계약이 그것을 금한다(`L1EventStream.stale` — 「true 면 UI 는
 *    마지막 값을 현재로 보이지 않는다」).
 */
export function resetStreamProgress(): void {
  executionBatches = new Map()
  if (
    state.byBatch.size === 0 &&
    state.byModule.size === 0 &&
    state.typeByModule.size === 0 &&
    state.byBatchModule.size === 0
  ) {
    return
  }
  publish(EMPTY)
}

export function getStreamProgress(): StreamProgressState {
  return state
}

export function subscribeStreamProgress(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// ── 읽기 ────────────────────────────────────────────────────────────────────

/**
 * 그 배치의 진행. **두 갈래를 여기서 합친다** — 스텝 수는 진행 스트림이, 시퀀스 이름은
 * 상태 스트림이 준다(계약이 그렇게 갈라 두었고 같은 소켓으로 함께 온다).
 *
 * ## 🔴 진행 프레임이 끊겨도 스텝 수를 잃지 않는다 (2026-09-22)
 *
 * 모듈이 **스스로 서면**(시약 소진 보류 등) `batch_progress` 가 **0건**이 된다. 종전에는 그때
 * 줄이 `module_status` 미러의 **시퀀스 이름만** 남기고 스텝 수를 버렸다 —
 * 「염색기 — dispense_reagent」. 미러에는 `step:56 · total:313` 이 그대로 있었는데도.
 * 🔴 **하필 가장 나쁜 때 가장 적게 말한 것**이라 잇는다.
 * ⚠ **잇는 것은 «서버가 준 수»뿐이다.** 「N분째 안 움직인다」 같은 판정은 만들지 않는다 —
 *   멈췄다는 사실은 서버가 `hold` 와 알람으로 말한다.
 */
export function selectBatchProgress(
  snapshot: StreamProgressState,
  batchId: string | undefined,
): BatchProgressView | undefined {
  if (batchId === undefined) return undefined
  const moduleSteps = selectBatchModuleSteps(snapshot, batchId)
  const progress = snapshot.byBatch.get(batchId)
  let mirror: ModuleExecution | undefined
  for (const exec of snapshot.byModule.values()) {
    if (exec.batchId === batchId) {
      mirror = exec
      break
    }
  }
  const sequence = mirror?.sequence
  if (progress === undefined) {
    /* 🔴 **재료가 하나도 없으면 요소를 안 그린다** — 빈 줄을 만들지 않는다 */
    if (mirror === undefined) return undefined
    if (sequence === undefined && mirror.step === undefined && mirror.total === undefined) {
      return undefined
    }
    /*
     * 🔴 **잇는 것은 «스텝 수»뿐이다 — 「어느 모듈이 도는가」는 잇지 않는다**(2026-09-22 정정).
     *    한 번 `moduleType` 까지 실었다가 `followRunningDevice` 시험이 잡았다: 그 값이
     *    **모니터링 따라가기의 판정 입력**이라, 미러로 채우면 **진행 프레임이 한 건도 없는데도
     *    「염색기가 돈다」**가 된다. 미러는 1초 주기 상태 거울이지 「돈다」는 말이 아니다.
     * ⇒ 두 축을 가른다. **스텝 수는 사실**이라 이어도 되고, **도는 모듈은 판정**이라 서버가
     *   진행 프레임으로 말하기 전에는 **모름**이다.
     */
    return {
      ...(sequence === undefined ? {} : { sequence }),
      ...(mirror.step === undefined ? {} : { step: mirror.step }),
      ...(mirror.total === undefined ? {} : { total: mirror.total }),
      ...(moduleSteps.length === 0 ? {} : { moduleSteps }),
    }
  }

  /*
   * 🔴 **모듈 종류를 여기서 붙인다.** 진행 프레임은 `module_id` 만 싣고(실물 전수 2026-09-20)
   *    종류는 `module_status` 가 나른다 — 계약이 두 축을 갈라 두었다. `sequence` 를 잇는 것과
   *    같은 결이다. 🔴 **못 찾으면 안 붙인다.** 「알 수 없는 모듈」 같은 말을 만들지 않는다.
   */
  const moduleType =
    progress.moduleId === undefined ? undefined : snapshot.typeByModule.get(progress.moduleId)

  if (sequence === undefined && moduleType === undefined && moduleSteps.length === 0) {
    return progress
  }
  return {
    ...progress,
    ...(sequence === undefined ? {} : { sequence }),
    ...(moduleType === undefined ? {} : { moduleType }),
    ...(moduleSteps.length === 0 ? {} : { moduleSteps }),
  }
}

/**
 * 그 배치에서 **지금까지 들은 모듈별 스텝 수.** 종류(`moduleType`)는 여기서 붙인다 —
 * 진행 프레임은 `module_id` 만 싣기 때문이다(`selectBatchProgress` 와 같은 결).
 *
 * 🔴 **못 들은 모듈은 줄 자체가 없다.** 「총수 0」으로 채우지 않는다 — 0 은 「스텝이 없다」는
 *    **틀린 사실**이고, 모르는 것은 모르는 채로 화면에 드러나야 한다(빈 칸).
 */
export function selectBatchModuleSteps(
  snapshot: StreamProgressState,
  batchId: string | undefined,
): readonly ModuleStepView[] {
  if (batchId === undefined) return []
  const inner = snapshot.byBatchModule.get(batchId)
  if (inner === undefined) return []
  return [...inner.values()].map((entry) => {
    const moduleType = snapshot.typeByModule.get(entry.moduleId)
    return moduleType === undefined ? entry : { ...entry, moduleType }
  })
}

/**
 * 작업 줄이 쓰는 훅.
 *
 * ⚠ **폴링이 아니다** — 프레임이 올 때만 깨어난다. 프레임이 없으면 타이머도 없다.
 */
export function useBatchProgress(batchId: string | undefined): BatchProgressView | undefined {
  const snapshot = useSyncExternalStore(
    subscribeStreamProgress,
    getStreamProgress,
    getStreamProgress,
  )
  return useMemo(() => selectBatchProgress(snapshot, batchId), [snapshot, batchId])
}

/**
 * **그 모듈이 지금 말하고 있는 진행.** 그림(장비 관점)이 쓴다.
 *
 * 🔴 **작업 줄 3행과 «같은 보관소»를 읽는다.** 작업 줄은 배치로, 그림은 모듈로 찾을 뿐이고
 *    프레임은 한 벌이다 — 그래서 두 곳이 서로 다른 말을 할 수 없다(그것이 규약이다).
 * 🔴 **가장 최근 것 하나를 고른다.** 한 모듈에 여러 배치의 진행이 남아 있을 수 있는데
 *    (종착해도 지워 줄 프레임이 없다) `put()` 이 최신을 맨 뒤로 보내므로 **뒤에서부터** 찾는다.
 * ⚠ 여기서 「돌고 있나」를 판정하지 않는다 — 마지막으로 들은 말을 줄 뿐이고,
 *   그것이 「지금」인지는 모듈 상태(`ModuleSnapshot.state`·`stale`)가 정한다.
 */
export function selectModuleProgress(
  snapshot: StreamProgressState,
  moduleId: string | undefined,
): BatchProgressView | undefined {
  if (moduleId === undefined) return undefined
  let found: BatchProgressView | undefined
  for (const view of snapshot.byBatch.values()) {
    if (view.moduleId === moduleId) found = view
  }
  return found
}

/** 그림이 쓰는 훅. `useBatchProgress` 와 같은 보관소·같은 구독이다 */
export function useModuleProgress(moduleId: string | undefined): BatchProgressView | undefined {
  const snapshot = useSyncExternalStore(
    subscribeStreamProgress,
    getStreamProgress,
    getStreamProgress,
  )
  return useMemo(() => selectModuleProgress(snapshot, moduleId), [snapshot, moduleId])
}

/**
 * **그 배치의 실행을 지금 쥐고 있다고 서버가 말한 모듈의 종류**(`module_status.current_execution.batch_id`
 * 미러 · 종류는 같은 프레임의 `module_type`). 없으면 `undefined` 다.
 *
 * 🔴 **작업 줄 구간의 「지금」 자리 전용이다** — 새로 고친 직후나 모듈이 보류로 서 있어 진행 프레임이
 *    0건일 때(2026-09-22 실측) 구간 셋 중 어느 것이 지금인지를 세운다(eric 2026-09-28).
 * 🔴 **`selectBatchProgress().moduleType` 에 섞지 않는다.** 그 칸은 모니터링 「따라가기」의 판정 입력이라
 *    미러로 채우면 진행 프레임이 없는데도 「돈다」가 된다(그 함수 머리말 2026-09-22 정정). 여기는 따로 읽는다.
 * ⚠ 새 조회가 아니다 — 이미 1Hz 로 오는 구독 미러를 읽을 뿐이다(DP-01).
 */
export function selectBatchRunningModuleType(
  snapshot: StreamProgressState,
  batchId: string | undefined,
): string | undefined {
  if (batchId === undefined) return undefined
  for (const [moduleId, exec] of snapshot.byModule) {
    if (exec.batchId === batchId) return snapshot.typeByModule.get(moduleId)
  }
  return undefined
}

/** 작업 줄이 쓰는 훅. `useBatchProgress` 와 같은 보관소·같은 구독이다 */
export function useBatchRunningModuleType(batchId: string | undefined): string | undefined {
  const snapshot = useSyncExternalStore(
    subscribeStreamProgress,
    getStreamProgress,
    getStreamProgress,
  )
  return useMemo(() => selectBatchRunningModuleType(snapshot, batchId), [snapshot, batchId])
}
